import { ScrollStory, StageCanvas, StageCard, StagePanels, type StoryBeat } from '../../../../components/ScrollStory';

/**
 * "How it works" for Phone Validation — submit → canonicalize → line-type lookup → cache → SMS
 * verdict. Grounded in the phone-validation code (PhoneValidationService.verify/createBulkJob,
 * VeriphoneClient phone_type enum, the (tenant, e164) unique cache key, the SMS_OK map on the
 * analytics tab). The provider under the hood is Veriphone; it canonicalizes and classifies, and
 * SieveGuard caches, bills, and derives the SMS-deliverability hint. There is no libphonenumber
 * in the API — the only local step is a regex used as a cache key, so the copy says so plainly.
 */

const STAGES = ['Submit', 'Canonicalize', 'Classify', 'Cache', 'SMS verdict', 'Surface'];

type Beat = StoryBeat & { detail: React.ReactNode };

const BEATS: Beat[] = [
  {
    n: 1,
    kicker: 'Submit',
    title: 'One number or a million',
    body: (
      <>
        Verify a single number at <span className="font-mono">POST /v1/phone-validation/verify</span>, or upload a CSV —
        one number per line, up to 1,000,000 rows. Bulk jobs run async and fire{' '}
        <span className="font-mono">phone_validation.bulk_completed</span> when they finish.
      </>
    ),
    facts: ['single or bulk', 'up to 1M rows', 'bulk_completed'],
    detail: (
      <StageCard head="Submit a number">
        <div className="font-mono text-[11px] md:text-[12.5px]">
          <div className="text-faint">POST /v1/phone-validation/verify</div>
          <div className="text-allow">{'{ "phone": "(415) 555-0132" }'}</div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">CSV bulk capped at 5MB · 1M rows</div>
      </StageCard>
    ),
  },
  {
    n: 2,
    kicker: 'Canonicalize',
    title: 'Down to one E.164 key',
    body: (
      <>
        Formatting is stripped and the number resolves to canonical E.164 — the authoritative parse comes from the
        provider, not a local library. Numbers we can&rsquo;t parse at all come back{' '}
        <span className="font-mono">syntax-error</span>, free, and cached so you never re-submit them.
      </>
    ),
    facts: ['→ E.164', 'provider is source of truth', 'syntax-error = free'],
    detail: (
      <StageCard head="Canonicalize">
        <div className="font-mono text-[11px] md:text-[12.5px]">
          <div className="text-faint">(415) 555-0132</div>
          <div className="text-allow">→ +14155550132</div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          unparseable → syntax-error · no credit charged
        </div>
      </StageCard>
    ),
  },
  {
    n: 3,
    kicker: 'Classify',
    title: 'Country, carrier, line type',
    body: (
      <>
        The lookup resolves country, carrier and the all-important line type —{' '}
        <span className="font-mono">mobile</span>, <span className="font-mono">voip</span>,{' '}
        <span className="font-mono">fixed_line</span>, <span className="font-mono">toll_free</span>,{' '}
        <span className="font-mono">premium_rate</span>, <span className="font-mono">shared_cost</span> or{' '}
        <span className="font-mono">unknown</span>.
      </>
    ),
    facts: ['country + carrier', '7 line types', 'honest "unknown"'],
    detail: (
      <StageCard head="Classification">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px] md:text-[12.5px]">
          <Kv k="country" v="US" />
          <Kv k="carrier" v="Verizon" />
          <Kv k="type" v="mobile" hot />
          <Kv k="valid" v="true" />
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">line type per provider classification</div>
      </StageCard>
    ),
  },
  {
    n: 4,
    kicker: 'Cache',
    title: 'Pay once per number',
    body: (
      <>
        Results are cached per workspace, keyed on <span className="font-mono">(tenant, E.164)</span> — checked before
        any billing. Re-import the same CRM next month and every repeat number returns for free; a bulk upload explodes
        straight into the same cache.
      </>
    ),
    facts: ['(tenant, E.164) key', 'checked before billing', 'bulk fills the cache'],
    detail: (
      <StageCard head="Cache-first">
        <div className="font-mono text-[11px] md:text-[12.5px]">
          <div className="text-faint">re-lookup +14155550132</div>
          <div className="text-allow">→ cache hit · 0 credits</div>
        </div>
        <div className="mt-1.5 font-mono text-[10.5px] text-faint md:text-[11px]">
          billable only on a first-time miss
        </div>
      </StageCard>
    ),
  },
  {
    n: 5,
    kicker: 'SMS verdict',
    title: 'Will an SMS actually land?',
    body: (
      <>
        The line type drives the deliverability read: only <span className="font-mono">mobile</span> is
        SMS-deliverable. VoIP, fixed-line, toll-free and the rest silently fail on most carriers — so junk numbers
        don&rsquo;t quietly burn your campaign spend.
      </>
    ),
    facts: ['mobile = deliverable', 'everything else fails', 'no silent burn'],
    detail: (
      <StageCard head="SMS deliverability">
        <div className="space-y-1 font-mono text-[10.5px] md:text-[11.5px]">
          <SmsRow type="mobile" ok />
          <SmsRow type="voip" />
          <SmsRow type="fixed_line" />
          <SmsRow type="toll_free" />
        </div>
      </StageCard>
    ),
  },
  {
    n: 6,
    kicker: 'Surface',
    title: 'Where the number shows up',
    body: (
      <>
        The per-number detail page shows the SMS hint for its line type; the analytics tab rolls a{' '}
        <span className="font-mono">SMS-deliverable share</span> tile — mobile lines as a percent of everything
        classified — so you see the health of a whole list at a glance.
      </>
    ),
    facts: ['per-number detail', 'SMS-deliverable share', 'filter by type'],
    detail: (
      <StageCard head="Analytics tile">
        <div className="flex items-end justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">SMS-deliverable</div>
            <div className="font-display text-3xl font-bold text-allow">68%</div>
          </div>
          <div className="font-mono text-[10.5px] text-faint md:text-[11px]">6,812 mobile / 10,000 classified</div>
        </div>
      </StageCard>
    ),
  },
];

export function PhoneValidationStory() {
  const panels = BEATS.map((b) => ({ n: b.n, content: b.detail }));
  return (
    <section className="mt-20" aria-label="How phone validation works">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-allow">
          <span className="h-1.5 w-1.5 rounded-full bg-allow" /> How it works
        </span>
        <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
          From a raw number to an <span className="font-mono text-allow">SMS verdict</span>
        </h2>
        <p className="mt-3 text-[14.5px] leading-relaxed text-muted">
          Follow <span className="font-mono">(415) 555-0132</span> through the pipeline — canonicalized to E.164,
          classified by line type, cached per workspace, and turned into a straight SMS-deliverable answer.
        </p>
      </div>

      <ScrollStory
        beats={BEATS}
        className="mt-6"
        renderStage={(beat, p) => (
          <StageCanvas>
            <div className="absolute inset-x-4 top-4 md:inset-x-6 md:top-6">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-faint md:text-[11px]">
                <span>Phone validation pipeline</span>
                <span>step {beat}/6</span>
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-border">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-allow to-accent transition-all duration-500"
                  style={{ width: `${(beat / 6) * 100}%` }}
                />
              </div>
              <div className="mt-2 font-mono text-[12px] text-allow md:text-[13px]">{STAGES[beat - 1]}</div>
              <span className="sr-only">{`Progress ${Math.round(p * 100)}%`}</span>
            </div>
            <StagePanels beat={beat} panels={panels} />
          </StageCanvas>
        )}
      />
    </section>
  );
}

function Kv({ k, v, hot }: { k: string; v: string; hot?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-faint">{k}</span>
      <span className={hot ? 'font-bold text-allow' : 'text-txt'}>{v}</span>
    </div>
  );
}

function SmsRow({ type, ok }: { type: string; ok?: boolean }) {
  return (
    <div
      className={`flex items-center justify-between rounded border px-2 py-1 ${
        ok ? 'border-allow/50 bg-allow/10 text-allow' : 'border-border text-muted'
      }`}
    >
      <span className="font-mono">{type}</span>
      <span className="uppercase tracking-[0.06em]">{ok ? 'deliverable' : 'silently fails'}</span>
    </div>
  );
}
